// Where today's addresses went (#874).
//
// Operate and Design took the pages that held both reading the network and
// changing it: Networks, Plan, Clients, Per-unit access, Compare and the work
// list became tabs of Operate's and Design's pages, and the device list moved
// under Operate. **Every address those pages answered at still lands on the
// same content**, at its new address and with its query string: the scope,
// and every facet the content reads. Device links are everywhere — an audit
// row, a dashboard widget, the console window, a bookmark — so this is not a
// courtesy for the few who typed an address by hand.
//
// A redirect replaces the entry rather than adding one: the address followed
// is the address meant, and the back button should not land on a page that
// only sends it forward again.
//
// This is a table and not a rule. `/plan` is the one old page that became two,
// so its `tab` facet decides which: the facet named a section of the plan
// (#519), and each section is now a tab, which a segment of the address names
// (`nav.ts`). Every other old address is one page, and its query moves whole —
// the comparison's own `tab` among it, which is a facet of that read and never
// was the plan's.

/** `/plan`'s sections, by the facet that named each, and where each is now.
 * An unknown value opened the first section, and still does. */
const PLAN_SECTIONS: Record<string, string> = {
  devices: "/design/physical",
  locations: "/design/physical/locations",
  // The plan import was retired (#2201), so its old facet opens the page.
  import: "/design/physical",
  topology: "/design/wired",
  ports: "/design/wired/ports",
};

/** Every other old address, and the page it moved to whole.
 *
 * `/operate/topology` is #1352's rather than #874's, and it is here for the
 * same reason the rest are: Operate's topology became a tab of Operate ›
 * Wired, so the address an operator bookmarked when it was a page of its own
 * lands on the same drawing where it now lives. A redirect was chosen over a
 * rename of the section precisely so that bookmark survives.
 *
 * **It targets the page's own address and not a `/topology` segment**, because
 * Topology is that page's first tab and a first tab is written as its absence
 * (`TabbedPage`). So the bookmark lands on the drawing, which is what it was
 * for. If the tab order ever changes, this line changes with it.
 *
 * `/design/wired/way-out` is #1480's: the tab was renamed *WAN* when "way
 * out" left the operator's vocabulary, and its segment with it, so an address
 * bookmarked under the old segment lands on the same editor.
 *
 * `/design/review` and its tabs are #1892's: Review became Changes, one inbox
 * whose Pending holds the work list and the venue's plan, and whose All
 * differences is the comparison. *Plan and apply* and *Work list* each land on
 * Pending, where the venue's row and every switch's are read and applied.
 *
 * `/design/stock` is #2194's: the page became *Warehouse*, its address with
 * it, while `/v1` keeps `stock`.
 *
 * `/design/physical/import` is #2201's: the plan import from a vendor export
 * was retired with its tab, so an address bookmarked on it lands on the page
 * rather than on "There is no such tab". */
const MOVED: Record<string, string> = {
  "/operate/topology": "/operate/wired",
  "/design/wired/way-out": "/design/wired/wan",
  "/design/physical/import": "/design/physical",
  "/devices": "/operate/devices",
  "/clients": "/operate/wireless",
  "/pans": "/operate/access",
  "/networks": "/design/wireless",
  "/comparison": "/design/changes/differences",
  "/work-list": "/design/changes",
  "/design/review": "/design/changes",
  "/design/review/compare": "/design/changes/differences",
  "/design/review/work-list": "/design/changes",
  "/design/review/reconcile": "/design/changes/reconcile",
  "/design/stock": "/design/warehouse",
};

/** The addresses this answers, for the router. */
export const MOVED_FROM: readonly string[] = [...Object.keys(MOVED), "/plan"];

/** Where an old address is now, with the query it should carry; undefined
 * for an address that did not move. */
export function movedTo(pathname: string, search: string): { pathname: string; search: string } | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (path === "/plan") {
    const q = new URLSearchParams(search);
    const to = PLAN_SECTIONS[q.get("tab") ?? ""] ?? PLAN_SECTIONS.devices!;
    q.delete("tab");
    const s = q.toString();
    return { pathname: to, search: s ? "?" + s : "" };
  }
  const to = MOVED[path];
  return to === undefined ? undefined : { pathname: to, search };
}
